// One leave request on the employee's Leaves tab, in the files list's look
// (record-row.tsx): the leave's status and type, its duration and dates, then
// the attached document with its own signing status and signers. Actions:
// "Pasirašyti" for the viewer's own pending signature, the leave calendar,
// and the document's preview and download. In the person's own cabinet the row
// also shows the request's comment and "Atšaukti prašymą" while it is pending.
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Ban, CalendarDays, Download, Eye } from "lucide-react";
import { openClickSign, openPdfPreview } from "components/modalx/modalx-openers";
import { API_URL } from "constants.js";
import { RootState } from "configureStore";
import { leaveCalendarLeaveTypeI18nKey } from "features/leave-calendar/leaveCalendar.constants";
import { buildLeaveCalendarPath } from "features/leave-calendar/leaveCalendarShareLink";
import { formatDateTime, formatDay } from "features/files/file-dates";
import { getStatusPill } from "features/files/file-row-model";
import { MetaList, Pill, StatusPillBadge } from "features/files/file-row-parts";
import RecordRow, { type RecordAction } from "features/files/record-row";
import type { SerializedLeaveRequest } from "services/leave-requests.service";
import { withdrawDocument } from "services/upload-file.service";
import CancelLeaveDialog from "./cancel-leave-dialog";
import {
  OWN_CANCEL_REASON,
  canCancelOwnLeave,
  canOpenLeavePdf,
  cancelErrorKey,
  getLeaveDocumentSigners,
  leaveStatusTone,
  leaveTimeRange,
} from "./leave-row-model";

const DURATION_LABEL_KEY: Record<SerializedLeaveRequest["duration"], string> = {
  full_day: "leave_calendar_duration_full",
  half_day: "leave_calendar_duration_half",
  hourly: "leave_calendar_duration_hourly",
};

type Props = {
  leave: SerializedLeaveRequest;
  companyId: string;
  // Someone else's card: no signing from here.
  readOnly: boolean;
  // The person's own cabinet: the comment and the cancel action are shown.
  isOwnCabinet?: boolean;
  // The request changed (cancelled, or found not to be cancellable): the list reloads.
  onChanged?: () => void;
};

export default function LeaveRow({ leave, companyId, readOnly, isOwnCabinet = false, onChanged }: Props) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const currentUserId = useSelector((state: RootState) => state.authReducer.role?.user);
  const [isConfirmingCancel, setIsConfirmingCancel] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);
  const [cancelError, setCancelError] = useState<string | null>(null);

  const signers = getLeaveDocumentSigners(leave, currentUserId, readOnly);
  // The attached document: `fileId` is its Files record id, `file` that record
  // as stored, which is the shape the files list reads.
  const documentId = leave.fileId;
  const documentStatus = leave.file && getStatusPill(leave.file, signers);
  const timeRange = leaveTimeRange(leave);

  const metaParts = [
    { key: "duration", node: t(DURATION_LABEL_KEY[leave.duration] || DURATION_LABEL_KEY.full_day) },
    { key: "dates", node: `${formatDay(leave.startDate)} – ${formatDay(leave.endDate)}` },
  ];
  if (timeRange) metaParts.push({ key: "time", node: timeRange });

  const actions: RecordAction[] = [
    {
      kind: "route",
      key: "calendar",
      label: t("profile_leave_view_in_calendar"),
      phoneLabel: t("profile_leave_calendar_short"),
      icon: CalendarDays,
      route: buildLeaveCalendarPath(companyId, leave.id),
    },
  ];
  if (documentId) {
    actions.push(
      {
        kind: "button",
        key: "view",
        label: t("view"),
        icon: Eye,
        disabled: !canOpenLeavePdf(leave),
        onClick: () => openPdfPreview(dispatch, documentId, companyId),
      },
      {
        kind: "download",
        key: "download",
        label: t("download"),
        icon: Download,
        disabled: !leave.file?.fileId,
        downloadUrl: `${API_URL}/${companyId}/files/download/${documentId}`,
      }
    );
  }

  if (isOwnCabinet && canCancelOwnLeave(leave, currentUserId)) {
    actions.push({
      kind: "button",
      key: "cancel",
      label: t("leave_cancel_action"),
      phoneLabel: t("leave_cancel_action_short"),
      icon: Ban,
      onClick: () => setIsConfirmingCancel(true),
    });
  }

  const cancelRequest = async () => {
    if (!documentId) return;
    setIsCancelling(true);
    setCancelError(null);
    try {
      await withdrawDocument(companyId, documentId, OWN_CANCEL_REASON);
      setIsConfirmingCancel(false);
    } catch (error) {
      setCancelError(cancelErrorKey(error));
    } finally {
      setIsCancelling(false);
      // Also after a refusal: the request may have been signed meanwhile, and the row should show that.
      onChanged?.();
    }
  };

  const row = (
    <RecordRow
      pill={
        <Pill tone={leaveStatusTone(leave.status)}>
          {t(`leave_calendar_status_${leave.status}`, { defaultValue: leave.status })}
        </Pill>
      }
      title={t(leaveCalendarLeaveTypeI18nKey(leave.leaveType), { defaultValue: leave.leaveType })}
      details={
        <>
          <MetaList parts={metaParts} />
          {leave.isRedacted && (
            <p className="mt-1 text-xs text-zinc-500">{t("leave_calendar_redacted_note")}</p>
          )}
          {isOwnCabinet && leave.comment && (
            <p className="mt-1 whitespace-pre-line break-words text-xs text-zinc-500">
              {t("leave_row_comment", { comment: leave.comment })}
            </p>
          )}
          {leave.rejectionReason && (
            <p className="mt-1 text-xs text-red-700">
              {t("files_signer_decline_reason", { reason: leave.rejectionReason })}
            </p>
          )}
          {documentId && (
            <div className="mt-1.5">
              <p className="text-sm leading-snug">
                {documentStatus && (
                  <>
                    <StatusPillBadge status={documentStatus} />{" "}
                  </>
                )}
                <span className="break-words font-medium text-zinc-800">
                  {leave.file?.title || leave.file?.name || t("file")}
                </span>
              </p>
              {leave.file?.createdAt && (
                <MetaList
                  parts={[
                    {
                      key: "created",
                      node: (
                        <span title={formatDateTime(leave.file.createdAt)}>
                          {formatDay(leave.file.createdAt)}
                        </span>
                      ),
                    },
                  ]}
                />
              )}
            </div>
          )}
        </>
      }
      signers={documentId ? signers : []}
      primary={
        documentId && signers.some((signer) => signer.canSign)
          ? { label: t("sign"), onClick: () => openClickSign(dispatch, documentId) }
          : null
      }
      actions={actions}
    />
  );

  return (
    <>
      {row}
      {isConfirmingCancel && (
        <CancelLeaveDialog
          isWorking={isCancelling}
          errorKey={cancelError}
          onConfirm={cancelRequest}
          onClose={() => {
            setIsConfirmingCancel(false);
            setCancelError(null);
          }}
        />
      )}
    </>
  );
}
